import { VideoJsAudioPlayer } from '@app/core/playback/audio/VideoJsAudioPlayer';
import { ExclusivePlaybackMode } from '@app/core/playback/store/base-playback-slice';
import { VideoJsPlaybackProvider } from '@app/core/playback/video/VideoPlaybackProvider';
import { PlaybackVideoJsPlayer } from '@app/core/video-player/PlaybackVideoJsPlayer';
import Player from 'video.js/dist/types/player';
import { disableVideoJsRemotePlayback } from './disable-media-remote-playback';

export interface UrlMediaPlaybackProps {
  /** Playable media URL (direct file or blob). */
  src: string;
  /** MIME type passed to Video.js for URLs without a file extension. */
  sourceType: string;
  /** Optional identifier for this playback instance in the registry. */
  playbackId?: string;
  exclusivePlaybackMode?: ExclusivePlaybackMode;
  /** When true, playback starts as soon as Video.js is ready. */
  autoPlay?: boolean;
  /** Called when playback starts. */
  onPlay?: () => void;
  /** Called when playback reaches the end of the media. */
  onEnded?: () => void;
}

/**
 * File-URL player registered with the playback registry so exclusive play works
 * with logger, live player, and other registered players. Audio uses the same
 * Video.js audio preset as fragments.
 */
export function UrlMediaPlayback({
  src,
  sourceType,
  playbackId,
  exclusivePlaybackMode = 'pause',
  autoPlay = false,
  onPlay,
  onEnded,
}: UrlMediaPlaybackProps) {
  const isAudio = sourceType.startsWith('audio/');

  const onReady = (player: Player) => {
    disableVideoJsRemotePlayback(player);
    if (onPlay) {
      player.on('play', onPlay);
    }
    if (onEnded) {
      player.on('ended', onEnded);
    }
  };

  const player = isAudio ? (
    <VideoJsAudioPlayer
      sourceType={sourceType}
      options={{ autoplay: autoPlay }}
      onReady={onReady}
    />
  ) : (
    <PlaybackVideoJsPlayer
      sourceType={sourceType}
      bordered
      options={{
        autoplay: autoPlay,
        controls: true,
        controlBar: {
          pictureInPictureToggle: false,
        },
      }}
      onReady={onReady}
    />
  );

  return (
    <VideoJsPlaybackProvider
      key={src}
      src={src}
      playbackId={playbackId}
      exclusivePlaybackMode={exclusivePlaybackMode}
    >
      {player}
    </VideoJsPlaybackProvider>
  );
}
